<template>
  <div
    id="viewerContainer"
    ref="containerRef"
    class="pdf-js-presenter this__viewport"
    :class="{ 'this__viewport--pinch-zoom': userControlled }"
  >
    <!-- the laserpointer dot -->
    <svg
      class="this__laserpointer"
      :style="laserpointerStyles"
      height="20"
      width="20"
    >
      <circle
        cx="10"
        cy="10"
        r="10"
        :fill="props.laserpointer?.color ?? '#db006e'"
      />
    </svg>
    <div id="viewer" ref="viewerElementRef" class="this__page pdfViewer"></div>
  </div>
</template>

<script lang="ts" setup>
import {
  watch,
  computed,
  ref,
  markRaw,
  onBeforeUnmount,
  CSSProperties,
  toValue,
  nextTick,
} from "vue";
import {
  computedAsync,
  useEventListener,
  useThrottleFn,
  useVModel,
  watchThrottled,
  useElementBounding,
  useScroll,
} from "@vueuse/core";
import { usePinch } from "@/composables";
import {
  LASER_HIDDEN,
  pointFromViewport,
  rectsAreClose,
  visibleRectFromViewport,
  wireRectToFitR,
} from "./PdfJsPresenter.zoom";
import type { LaserPointer } from "@/models";

// Import PDF.JS
import * as pdfjsLib from "pdfjs-dist";
import * as pdfjsViewer from "pdfjs-dist/web/pdf_viewer.mjs";

const BASE_URL: string | null = null;

const TextLayerMode = {
  DISABLE: 0,
  ENABLE: 1,
  ENABLE_PERMISSIONS: 2,
};

interface RawDims {
  pageWidth: number;
  pageHeight: number;
  pageX: number;
  pageY: number;
}

interface ZoomRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

interface Props {
  document: string | URL;
  page?: number;
  enableTextSelection?: boolean;
  enableAnnotations?: boolean;
  zoom?: ZoomRect | null;
  laserpointer?: LaserPointer;
  interceptInternalLink?: (toPage: number, proceed: () => void) => void;
  /**
   * Identifies the logical sender slide when document/page alone is not
   * unique. Changing it invalidates active laser gestures and queued points.
   */
  senderContext?: string | number;
  /**
   * When true, the presenter becomes a zoom *sender*: wheel (with Ctrl/Cmd),
   * trackpad pinch, and touch pinch change the local view and emit
   * `update:zoom` so a parent can mirror the rect to other receivers.
   */
  userControlled?: boolean;
}

// Setup PDF.JS Worker
// We must load the PDF.JS worker to make the viewer work.
// We use a `postinstall` script in the `package.json` to copy the worker script to the right place.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "/pdf.worker.mjs",
  window.location.href,
).href;

const props = withDefaults(defineProps<Props>(), {
  page: 1,
  enableAnnotations: false,
  enableTextSelection: false,
  zoom: () => ({ top: 0, left: 0, bottom: 1, right: 1 }),
  laserpointer: undefined,
  // allows the calling component to intercept link clicks, e.g. to properly handle magic pages.
  interceptInternalLink: undefined,
  senderContext: undefined,
  userControlled: false,
});

// Load the PDF document with PDF.JS.
const pdf = computedAsync(async (onCancel) => {
  let cancelled = false;
  try {
    const loadingTask = pdfjsLib.getDocument({
      url: props.document,
      cMapUrl: new URL("/cmaps/", window.location.href).href,
      wasmUrl: new URL("/pdfjs-wasm/", window.location.href).href,
      cMapPacked: true,
      enableXfa: false, // disable Xfa forms
      enableHWA: true, // enable hardware acceleration
    });
    onCancel(() => {
      cancelled = true;
    });
    const doc = await loadingTask.promise.catch(async (error) => {
      await loadingTask.destroy();
      throw error;
    });
    if (cancelled) {
      // Dispose a late result without attaching it. PDF.js 5.1 can raise an
      // unhandled worker error when destroyed while its initial fetch is pending.
      await doc.destroy();
      return;
    }
    // The document object must NEVER be made reactive, because this breaks PDF.JS.
    // That's why we mark the document to be `raw`, so Vue will not make it deeply reactive.
    // The computed reference `pdf` will be reactive. However, changes within it, will not.
    return markRaw(doc);
  } catch (e) {
    if (cancelled) return;
    console.error(e);
    emit("error");
  }
});

// Setup PDF.JS event bus.
const eventBus = new pdfjsViewer.EventBus();
const wrappedDispatch = eventBus.dispatch.bind(eventBus);
eventBus.dispatch = function (eventName: string, data: unknown) {
  return wrappedDispatch(eventName, data as any);
};

// Setup PDF.JS link service.
// The link service allows to intercept link clicks.
// This is important for the handling of magic pages.
const linkService = new pdfjsViewer.SimpleLinkService({ eventBus });
const wrappedGoToDestination = linkService.goToDestination.bind(linkService);

linkService.goToDestination = async function (dest: string | any[] | null) {
  const doc = toValue(pdf);
  if (!doc) throw new Error("document is not ready");
  if (typeof dest === "string") dest = await doc.getDestination(dest);
  if (dest === null) throw new Error("destination is null");

  if (typeof dest === "object" && dest !== null) {
    const pageIndex = await doc.getPageIndex(dest[0]);
    goToPage(pageIndex + 1); // take care, pages are zero-indexed
  } else if (typeof dest === "number" && Number.isInteger(dest)) {
    goToPage(dest + 1); // take care, pages are zero-indexed
  } else {
    return wrappedGoToDestination(dest);
  }
};

linkService.goToPage = async function (pageNumberOrLabel: number | string) {
  if (typeof pageNumberOrLabel === "string") {
    const doc = toValue(pdf);
    if (!doc) throw new Error("document is not ready");
    const labels = await doc.getPageLabels();
    if (!labels) {
      throw Error(
        `goToPage() called with label ${pageNumberOrLabel}, but the document does not support page labels.`,
      );
    }
    const pageIndex = labels.indexOf(pageNumberOrLabel);
    if (pageIndex < 0) {
      throw Error(
        `current document has no page with label ${pageNumberOrLabel}`,
      );
    }
    pageNumberOrLabel = pageIndex + 1; // take care, pages are zero-indexed
  }
  goToPage(pageNumberOrLabel);
};

function goToPage(pageNumber: number) {
  if (props.interceptInternalLink)
    // interceptInternalLink might be a callback to handle magic pages
    props.interceptInternalLink(
      pageNumber,
      () => (currentPageNumber.value = pageNumber),
    );
  else currentPageNumber.value = pageNumber;
}

async function getDestArrayForZoom(
  viewer: pdfjsViewer.PDFSinglePageViewer,
  pageNumber: number,
  zoom?: ZoomRect | null,
): Promise<any[] | undefined> {
  // This is how the zoom rectangle is calculated in iOS:
  // https://github.com/Pitchview/Projector_iOS/blob/developer/PViOS/PViOS/UIModules/Presentation/PDFView%2BHelpers.swift

  // Withouth any zoom, we will fit the whole page.
  if (!zoom) return [pageNumber, { name: "Fit" }];

  // If the zoom rectangle is the whole page or even larger, we will fit the whole page.
  let { top, left, bottom, right } = zoom;
  if (
    top == null ||
    left == null ||
    bottom == null ||
    right == null || // All these checks include `undefined` and `null` by intention.
    bottom <= top ||
    right <= left
  ) {
    console.warn("Invalid zoom rect:", { top, left, bottom, right });
    // Fix invalid zoom rectangles, match the whole page.
    [top, left, bottom, right] = [0, 0, 1, 1];
  }
  if (top <= 0 && left <= 0 && bottom >= 1 && right >= 1) {
    return [pageNumber, { name: "Fit" }];
  }

  // If we cannot load the page yet, we prepare to fit the whole page.
  const page = await viewer.pdfDocument?.getPage(pageNumber ?? 1);
  if (!page) {
    console.warn("Page not loaded yet, prepare to fit the whole page.");
    return [pageNumber, { name: "Fit" }];
  }

  const viewport = page.getViewport({ scale: 1 });

  // FitR uses PDF user space (Y bottom-up) and requires
  // [left, bottom, right, top] with bottom < top. wireRectToFitR converts the
  // top-down wire rect and adds the viewBox origin (pageX/pageY) so PDFs with a
  // non-zero crop/media box align correctly. Getting the ordering right matters
  // in particular for rotation 90/270 — pdf.js' FitR handler swaps
  // (width, height) before computing the scroll target, and only produces the
  // correct viewport offset when the input height is non-negative.
  const fitR = wireRectToFitR(
    { top, left, bottom, right },
    viewport.rawDims as RawDims,
  );

  return [
    pageNumber,
    { name: "FitR" },
    /* left   */ fitR.left,
    /* bottom */ fitR.bottom,
    /* right  */ fitR.right,
    /* top    */ fitR.top,
  ];
}

const emit = defineEmits<{
  (e: "update:page", page: number): void;
  (e: "update:numberOfPages", numberOfPages: number): void;
  (e: "update:zoom", value: ZoomRect): void;
  (e: "update:laserpointer", value: LaserPointer): void;
  (e: "loaded", url?: Props["document"]): void;
  (e: "rendered", pageNumber: number, document: Props["document"]): void;
  (e: "error"): void;
}>();

const containerRef = ref<HTMLDivElement>();
const currentPageNumber = useVModel(props, "page", emit, { passive: true });
const viewerElementRef = ref<HTMLDivElement>();
const {
  width: containerWidth,
  height: containerHeight,
  left: containerLeft,
  top: containerTop,
} = useElementBounding(containerRef);
const { y: containerScrollTop, x: containerScrollLeft } =
  useScroll(containerRef);
const {
  left: viewerLeft,
  top: viewerTop,
  height: viewerHeight,
  width: viewerWidth,
} = useElementBounding(viewerElementRef);

// Setup PDF.JS viewer when the required DOM elements get ready.
const viewerRef = computed(() => {
  const container = toValue(containerRef);
  const viewerElement = toValue(viewerElementRef);
  if (!container || !viewerElement) return null;
  const singlePageViewer = new pdfjsViewer.PDFSinglePageViewer({
    container,
    eventBus,
    viewer: viewerElement,
    linkService: linkService,
    textLayerMode: props.enableTextSelection
      ? TextLayerMode.ENABLE
      : TextLayerMode.DISABLE,
    annotationMode: props.enableAnnotations
      ? pdfjsLib.AnnotationMode.ENABLE
      : pdfjsLib.AnnotationMode.DISABLE,
  });
  markRaw(singlePageViewer);
  return singlePageViewer;
});

// As soon as `pdf` is available, we will load the current page.
const currentPage = computedAsync(async () => {
  const doc = toValue(pdf);
  if (!doc) return undefined;
  const pageNumber = Math.max(
    1,
    Math.min(currentPageNumber.value, doc.numPages),
  );
  return await doc.getPage(pageNumber);
});

// As soo as `pdf` is available, we will set how many pages we have.
const numberOfPages = computed(() => pdf.value?.numPages ?? undefined);

// This computed property is inserted into the `:style="..."` of the laserpointer.
const laserpointerStyles = computed<CSSProperties>(() => {
  const viewer = toValue(viewerRef);
  const page = toValue(currentPage);
  const point = props.laserpointer;
  if (!page || !point || !viewer || (point.x === -1 && point.y === -1))
    // By convention, a laserpointer with (-1, -1) means "no laserpointer"
    return {
      display: "none",
    };

  const pageview = viewer.getPageView(viewer.currentPageNumber - 1);
  const { pageWidth, pageHeight, pageX, pageY } = pageview.viewport
    .rawDims as RawDims;
  // convertToViewportPoint expects absolute PDF user space, so offset the
  // normalized wire point by the viewBox origin (pageX/pageY) — otherwise the
  // dot is misplaced on PDFs whose crop/media box does not start at (0,0).
  let [x, y] = pageview.viewport.convertToViewportPoint(
    Math.round(pageX + point.x * pageWidth),
    Math.round(pageY + point.y * pageHeight),
  );

  if ([0, 180].includes(pageview.viewport.rotation)) {
    y = toValue(viewerHeight) - y; // flip y-axis
  } else if ([90, 270].includes(pageview.viewport.rotation)) {
    x = toValue(viewerWidth) - x; // flip x-axis
  }

  x +=
    toValue(containerScrollLeft) + toValue(viewerLeft) - toValue(containerLeft);
  y += toValue(containerScrollTop) + toValue(viewerTop) - toValue(containerTop);
  return { top: `${y}px`, left: `${x}px` };
});

watch([pdf, viewerRef], ([newPdf, newViewer], [oldPdf, oldViewer]) => {
  if (oldViewer && (newPdf !== oldPdf || newViewer !== oldViewer)) {
    // @ts-expect-error PDF.js supports null to detach; its bundled type omits it.
    oldViewer.setDocument(null);
  }
  if (newPdf && newViewer) {
    newViewer.setDocument(newPdf);
    linkService.setViewer(newViewer);
    linkService.setDocument(newPdf, BASE_URL);
    emit("loaded", props.document);
  }
  if (newPdf !== oldPdf && oldPdf) {
    void oldPdf.destroy();
  }
  if (newViewer !== oldViewer && oldViewer) {
    oldViewer.cleanup();
  }
});

// ---------------------------------------------------------------------------
// Sender mode (userControlled):
// PDF.js' own viewport state (currentScale + container scroll) is the source
// of truth. Wheel / pinch / buttons drive it directly; a single throttled
// computeAndEmit() reads the visible region back via the viewport's own
// transforms (rotation-aware) and emits the wire rect. Because the sender
// never re-applies its own emitted rect, no echo-suppression is needed, and
// concurrent user inputs (pinch + drag, scroll while zoomed) converge on the
// same read path without races.
//
// Rotations 0/90/180/270 are handled uniformly via viewport.convertToPdfPoint
// / convertToViewportPoint — no per-rotation branching is required.
// ---------------------------------------------------------------------------

const MIN_SCALE = 0.1;
const MAX_SCALE = 10;

type SenderPageView = {
  div?: HTMLElement;
  viewport?: {
    width: number;
    height: number;
    viewBox: readonly [number, number, number, number];
    convertToPdfPoint: (x: number, y: number) => readonly [number, number];
    convertToViewportPoint: (x: number, y: number) => readonly [number, number];
  };
};

function computeVisibleZoomRect(
  viewer: pdfjsViewer.PDFSinglePageViewer,
  pageNumber: number,
): ZoomRect | null {
  const pageView = viewer.getPageView(pageNumber - 1) as
    | SenderPageView
    | undefined;
  const container = toValue(containerRef);
  if (!pageView?.div || !pageView.viewport || !container) return null;
  return visibleRectFromViewport(
    pageView.div.getBoundingClientRect(),
    container.getBoundingClientRect(),
    pageView.viewport,
  );
}

const throttledComputeAndEmit = useThrottleFn(
  () => {
    if (!props.userControlled) return;
    const viewer = toValue(viewerRef);
    if (!viewer) return;
    const rect = computeVisibleZoomRect(viewer, currentPageNumber.value);
    if (!rect) return;
    // Dedup against the current prop (which reflects what we last emitted via
    // v-model round-trip): skip when nothing changed at sub-pixel precision.
    if (props.zoom && rectsAreClose(rect, props.zoom)) return;
    emit("update:zoom", rect);
  },
  80,
  true,
);

/**
 * Zoom by `factor` around a screen-space anchor point, preserving the anchor
 * under the user's finger/cursor. Uses PDF.js' own transforms so all
 * rotations are handled uniformly.
 */
function applyScaleAroundAnchor(
  factor: number,
  anchorClientX: number,
  anchorClientY: number,
) {
  const viewer = toValue(viewerRef);
  const container = toValue(containerRef);
  if (!viewer || !container) return;
  const pageIndex = currentPageNumber.value - 1;
  const before = viewer.getPageView(pageIndex) as SenderPageView | undefined;
  if (!before?.div || !before.viewport) return;
  const beforeRect = before.div.getBoundingClientRect();
  const [pdfX, pdfY] = before.viewport.convertToPdfPoint(
    anchorClientX - beforeRect.left,
    anchorClientY - beforeRect.top,
  );
  const currentScale = viewer.currentScale;
  // Floor at the page-fit scale so the sender can never shrink the page
  // below its container — anything smaller would leave empty space the
  // receiver couldn't reproduce. The fit scale uses the *rotated* viewport
  // dims so it is correct for all four rotations.
  let minScale = MIN_SCALE;
  if (
    before.viewport.width > 0 &&
    before.viewport.height > 0 &&
    container.clientWidth > 0 &&
    container.clientHeight > 0 &&
    currentScale > 0
  ) {
    const unscaledW = before.viewport.width / currentScale;
    const unscaledH = before.viewport.height / currentScale;
    minScale = Math.max(
      MIN_SCALE,
      Math.min(
        container.clientWidth / unscaledW,
        container.clientHeight / unscaledH,
      ),
    );
  }
  const newScale = Math.min(
    MAX_SCALE,
    Math.max(minScale, currentScale * factor),
  );
  if (Math.abs(newScale - currentScale) < 1e-4) return;
  viewer.currentScale = newScale;
  const after = viewer.getPageView(pageIndex) as SenderPageView | undefined;
  if (!after?.div || !after.viewport) return;
  const [newVpX, newVpY] = after.viewport.convertToViewportPoint(pdfX, pdfY);
  const afterRect = after.div.getBoundingClientRect();
  container.scrollLeft += afterRect.left + newVpX - anchorClientX;
  container.scrollTop += afterRect.top + newVpY - anchorClientY;
  throttledComputeAndEmit();
}

function applyZoomFactor(
  factor: number,
  evt?: { clientX: number; clientY: number },
) {
  const container = toValue(containerRef);
  let anchorClientX: number;
  let anchorClientY: number;
  if (evt) {
    anchorClientX = evt.clientX;
    anchorClientY = evt.clientY;
  } else if (container) {
    const cRect = container.getBoundingClientRect();
    anchorClientX = cRect.left + cRect.width / 2;
    anchorClientY = cRect.top + cRect.height / 2;
  } else {
    return;
  }
  applyScaleAroundAnchor(factor, anchorClientX, anchorClientY);
}

function resetZoom() {
  const viewer = toValue(viewerRef);
  if (!viewer) return;
  viewer.currentScaleValue = "page-fit";
  throttledComputeAndEmit();
}

defineExpose({
  applyZoomFactor,
  resetZoom,
});

// Wheel + Ctrl/Cmd. macOS trackpad pinch arrives here with ctrlKey=true.
useEventListener(
  containerRef,
  "wheel",
  (evt: WheelEvent) => {
    if (!props.userControlled) return;
    if (!evt.ctrlKey && !evt.metaKey) return;
    evt.preventDefault();
    noteZoomGesture();
    let delta = evt.deltaY;
    if (evt.deltaMode === WheelEvent.DOM_DELTA_LINE) delta *= 15;
    else if (evt.deltaMode === WheelEvent.DOM_DELTA_PAGE) delta *= 150;
    // delta > 0 (scroll down) → zoom out (factor < 1).
    applyScaleAroundAnchor(Math.pow(0.999, delta), evt.clientX, evt.clientY);
  },
  { passive: false },
);

// Touch pinch via Pointer Events (composable in @/composables/usePinch).
usePinch(containerRef, ({ scale, centerX, centerY }) => {
  if (!props.userControlled) return;
  const container = toValue(containerRef);
  if (!container) return;
  const cRect = container.getBoundingClientRect();
  // scale > 1 (fingers spreading) → zoom in (factor > 1).
  applyScaleAroundAnchor(scale, cRect.left + centerX, cRect.top + centerY);
});

// Pan / scroll: emit the new visible rect so receivers follow.
watch([containerScrollLeft, containerScrollTop], () => {
  if (!props.userControlled) return;
  throttledComputeAndEmit();
});

// Container resize: re-emit so receivers match the new visible aspect.
watch([containerWidth, containerHeight], () => {
  if (!props.userControlled) return;
  throttledComputeAndEmit();
});

// Sender-side page change: navigate and fit. (The receiver watchThrottled
// below handles navigation + apply for non-userControlled instances.)
watch(currentPageNumber, () => {
  if (!props.userControlled) return;
  const viewer = toValue(viewerRef);
  if (!viewer) return;
  const page = currentPageNumber.value;
  viewer.scrollPageIntoView({
    pageNumber: page,
    destArray: [page, { name: "Fit" }],
  });
});

// ---------------------------------------------------------------------------
// Laserpointer sender (userControlled): while Ctrl/Cmd is held, the cursor
// position is emitted as a normalized wire point. Releasing the modifier
// starts a LASER_LINGER_MS linger, after which the hidden sentinel is sent.
// No mouse button is involved, which sidesteps text selection, Mac's
// secondary-click translation, and accidental link activation. The sender
// sees its own dot via the same `props.laserpointer` round-trip the receiver
// uses, so no special render path is needed.
// ---------------------------------------------------------------------------
const LASER_LINGER_MS = 4000;
const LASER_THROTTLE_MS = 50;
// Ctrl/Cmd is shared by zoom (wheel) and the laser (mousemove). A wheel-zoom
// records its time here so the laser stays suppressed for the duration of the
// zoom plus a short cooldown, instead of flashing on the stray mousemove that
// rides along with Ctrl+wheel.
const ZOOM_LASER_SUPPRESS_MS = 350;

let laserActive = false;
let laserGestureEverActive = false;
let laserCursor: { x: number; y: number } | null = null;
let laserLingerTimer: ReturnType<typeof setTimeout> | null = null;
let lastZoomAt = 0;

function clearLaserLinger() {
  if (laserLingerTimer !== null) {
    clearTimeout(laserLingerTimer);
    laserLingerTimer = null;
  }
}

// Called from the wheel-zoom path. Records the gesture time and force-hides any
// currently visible laser, so Ctrl+wheel zooming never leaves the dot showing.
function noteZoomGesture() {
  lastZoomAt = performance.now();
  if (!laserActive) return;
  laserActive = false;
  clearLaserLinger();
  if (laserGestureEverActive) {
    laserGestureEverActive = false;
    emit("update:laserpointer", { ...LASER_HIDDEN });
  }
}

function zoomSuppressesLaser() {
  return performance.now() - lastZoomAt < ZOOM_LASER_SUPPRESS_MS;
}

const emitLaserPointThrottled = useThrottleFn(
  (p: LaserPointer) => {
    // Guard against a trailing emit firing after the gesture already ended.
    // Every abrupt hide (page change, projector exit) sets laserActive = false
    // before emitting the hidden sentinel, so a point queued just before the
    // hide must not re-show the dot on the new slide ~50ms later.
    if (!laserActive) return;
    emit("update:laserpointer", p);
  },
  LASER_THROTTLE_MS,
  true,
);

function emitLaserCurrent() {
  if (!laserActive || !laserCursor) return;
  const viewer = toValue(viewerRef);
  if (!viewer) return;
  const pageIndex = currentPageNumber.value - 1;
  const view = viewer.getPageView(pageIndex) as SenderPageView | undefined;
  if (!view?.div || !view.viewport) return;
  const point = pointFromViewport(
    view.div.getBoundingClientRect(),
    laserCursor.x,
    laserCursor.y,
    view.viewport,
  );
  if (!point) return; // cursor outside the page — hold last position
  laserGestureEverActive = true;
  emitLaserPointThrottled({ x: point.x, y: point.y });
}

function startLaserLinger() {
  clearLaserLinger();
  laserLingerTimer = setTimeout(() => {
    laserLingerTimer = null;
    laserGestureEverActive = false;
    emit("update:laserpointer", { ...LASER_HIDDEN });
  }, LASER_LINGER_MS);
}

function setLaserActive(now: boolean) {
  if (now === laserActive) return;
  laserActive = now;
  if (now) {
    clearLaserLinger();
    emitLaserCurrent();
  } else if (laserGestureEverActive && laserLingerTimer === null) {
    startLaserLinger();
  }
}

// Only bind the global laser-gesture listeners while in sender mode. On
// receivers (and stacked non-top documents) there is nothing to emit, so this
// avoids attaching a window-level mousemove handler per presenter instance.
const laserWindowTarget = computed(() =>
  props.userControlled ? window : null,
);

// Mouse movement is the canonical activation signal: each event carries the
// current modifier state, so we don't need to track keydown/keyup separately
// just to keep modifier state in sync.
useEventListener(laserWindowTarget, "mousemove", (ev: MouseEvent) => {
  laserCursor = { x: ev.clientX, y: ev.clientY };
  setLaserActive((ev.ctrlKey || ev.metaKey) && !zoomSuppressesLaser());
  if (laserActive) emitLaserCurrent();
});

// keydown lets the laser appear without waiting for the next mousemove, as
// long as the cursor was seen at least once.
useEventListener(laserWindowTarget, "keydown", (ev: KeyboardEvent) => {
  if (ev.key !== "Control" && ev.key !== "Meta") return;
  if (laserCursor && !zoomSuppressesLaser()) setLaserActive(true);
});

// keyup deactivates immediately, in case the user doesn't move the mouse
// after releasing the modifier.
useEventListener(laserWindowTarget, "keyup", (ev: KeyboardEvent) => {
  if (ev.key !== "Control" && ev.key !== "Meta") return;
  setLaserActive(ev.ctrlKey || ev.metaKey); // false once the last modifier is up
});

// Losing focus mid-gesture (Alt-Tab, devtools) must end the gesture cleanly.
useEventListener(laserWindowTarget, "blur", () => {
  setLaserActive(false);
});

// Page change: hide immediately — the wire point is in unrotated PDF logical
// coordinates and would land somewhere unrelated on the new page.
function resetLaserGesture(clearCursor = false) {
  clearLaserLinger();
  laserActive = false;
  if (clearCursor) laserCursor = null;
  if (laserGestureEverActive) {
    laserGestureEverActive = false;
    emit("update:laserpointer", { ...LASER_HIDDEN });
  }
}

watch(currentPageNumber, () => {
  if (!props.userControlled) return;
  resetLaserGesture();
});

// Custom Flows may contain the exact same PDF page more than once. In that
// case neither document nor page changes, so the parent supplies a logical
// sender context to invalidate queued points at the item boundary.
watch(
  () => props.senderContext,
  async (newContext, oldContext) => {
    if (!props.userControlled || newContext === oldContext) return;
    resetLaserGesture();
    await nextTick();
    if (!props.userControlled || props.senderContext !== newContext) return;
    resetZoom();
  },
);

// Turning userControlled off (projector exit) drops all gesture state.
watch(
  () => props.userControlled,
  (enabled) => {
    if (enabled) return;
    resetLaserGesture(true);
  },
);

// Receiver path: apply the incoming wire rect via FitR. Skipped on the
// sender, whose viewer state is already the source of truth.
watchThrottled(
  [
    containerWidth,
    containerHeight,
    viewerRef,
    () => {
      if (!props.zoom) return null;
      else return { ...props.zoom };
    },
    currentPageNumber,
  ],
  async ([_width, _height, viewer, zoom, page]) => {
    if (!viewer) return;
    if (props.userControlled) return;
    const destArray = await getDestArrayForZoom(viewer, page, zoom);
    if (!destArray) {
      viewer.currentScaleValue = "page-fit";
      viewer.update();
    } else {
      // doc for scrollPageIntoView: https://github.com/mozilla/pdf.js/blob/master/web/pdf_viewer.js#L1508
      viewer.scrollPageIntoView({ pageNumber: page, destArray });
    }
  },
  { throttle: 100, trailing: true, leading: true },
);

watch(currentPage, async (_newPage, oldPage) => {
  if (oldPage) oldPage.cleanup();
});

watch(numberOfPages, (newNumberOfPages) => {
  if (newNumberOfPages) {
    emit("update:numberOfPages", newNumberOfPages);
    if (newNumberOfPages < currentPageNumber.value) currentPageNumber.value = 1;
  }
});

eventBus.on("pagesinit", async () => {
  const viewer = toValue(viewerRef);
  if (!viewer) throw new Error("pdfViewer is not ready");
  viewer.currentScaleValue = "page-fit";
  const pageNumber = toValue(currentPageNumber) ?? 1;
  const destArray = await getDestArrayForZoom(viewer, pageNumber, props.zoom);
  viewer.scrollPageIntoView({
    pageNumber,
    destArray,
  });
});

eventBus.on(
  "pagerendered",
  (event: { pageNumber: number; error?: unknown }) => {
    if (event.error) emit("error");
    else emit("rendered", event.pageNumber, props.document);
  },
);

onBeforeUnmount(() => {
  // Cancel page/text-layer work while the viewer refs are still available.
  // @ts-expect-error PDF.js supports null to detach; its bundled type omits it.
  toValue(viewerRef)?.setDocument(null);
  linkService.setDocument(null);
  const doc = toValue(pdf);
  const page = toValue(currentPage);
  if (page) {
    page.cleanup();
  }
  if (doc) {
    void doc.destroy();
  }
});
</script>

<style lang="scss">
.this__viewport {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;

  display: grid;
  justify-items: center;
  align-items: center;
}

// In sender mode, allow single-finger pan/scroll but suppress the browser's
// native pinch-zoom so the usePinch handler can drive the PDF zoom instead.
.this__viewport--pinch-zoom {
  touch-action: pan-x pan-y;
}

.pdf-js-presenter .this__laserpointer {
  position: absolute;
  margin-left: -10px;
  margin-right: -10px;
  margin-bottom: -10px;
  z-index: 100;
}

.pdf-js-presenter {
  @include meta.load-css("../../node_modules/pdfjs-dist/web/pdf_viewer.css");
}
</style>
